import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Video, PhoneMissed, PhoneOff, PhoneIncoming, PhoneOutgoing } from 'lucide-react';
import api from '@/lib/api';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { MemberAvatar } from '@/components/MemberAvatar';
import logger from '@/lib/logger';

function formatDuration(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m > 0 ? `${m}m ${r}s` : `${r}s`;
}

function relativeTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

const OUTCOME_META = {
  completed: { Icon: Video, color: 'text-emerald-600', dot: 'bg-emerald-500' },
  missed:    { Icon: PhoneMissed, color: 'text-rose-600', dot: 'bg-rose-500' },
  declined:  { Icon: PhoneOff, color: 'text-slate-500', dot: 'bg-slate-400' },
  cancelled: { Icon: PhoneOff, color: 'text-slate-500', dot: 'bg-slate-400' },
};

/**
 * Dashboard widget: shows the user's 5 most recent calls across every
 * conversation. Subscribes to ``call_summary`` WS events so it refreshes
 * live whenever a call ends. Clicking any row deep-links to /messages?dm=X.
 */
export function RecentCallsPanel() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addListener } = useWebSocket();
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.getRecentCalls(5)
      .then(r => setCalls(r.data || []))
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!addListener) return;
    const unsub = addListener('call_summary', () => load());
    return () => { unsub?.(); };
  }, [addListener, load]);

  return (
    <div className="border border-slate-200 rounded-sm bg-white" data-testid="recent-calls-panel">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Video className="w-3.5 h-3.5 text-[#0047AB]" />
          <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium">Recent Calls</p>
        </div>
        <button
          onClick={() => navigate('/messages')}
          className="text-[10px] text-slate-400 hover:text-[#0047AB] uppercase tracking-wider"
          data-testid="recent-calls-view-all"
        >
          View all →
        </button>
      </div>
      <div className="divide-y divide-slate-100">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full" />
          </div>
        ) : calls.length === 0 ? (
          <div className="px-4 py-10 text-center">
            <Video className="w-7 h-7 text-slate-200 mx-auto mb-2" />
            <p className="text-xs text-slate-400">No calls yet</p>
            <p className="text-[10px] text-slate-300 mt-0.5">Start one from any conversation.</p>
          </div>
        ) : (
          calls.map(c => {
            const outcome = c.call?.outcome || 'completed';
            const { Icon, color, dot } = OUTCOME_META[outcome] || OUTCOME_META.completed;
            const isOutbound = c.direction === 'outbound';
            const label = outcome === 'completed'
              ? `Video call · ${formatDuration(c.call?.duration_seconds)}`
              : outcome === 'missed'
                ? (isOutbound ? 'No answer' : 'Missed call')
                : outcome === 'declined'
                  ? (isOutbound ? 'Declined' : 'You declined')
                  : (isOutbound ? 'You cancelled' : 'Caller cancelled');
            return (
              <button
                key={c.id}
                onClick={() => navigate(`/messages?dm=${c.partner_id}`)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-50 transition-colors group"
                data-testid={`recent-call-${c.id}`}
              >
                <div className="relative shrink-0">
                  <MemberAvatar userId={c.partner_id} name={c.partner_name} size="sm" />
                  <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${dot} flex items-center justify-center`}>
                    {isOutbound
                      ? <PhoneOutgoing className="w-2 h-2 text-white" strokeWidth={3} />
                      : <PhoneIncoming className="w-2 h-2 text-white" strokeWidth={3} />}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-slate-900 truncate">{c.partner_name}</p>
                  <div className={`flex items-center gap-1 text-[11px] ${color}`}>
                    <Icon className="w-3 h-3 shrink-0" />
                    <span className="truncate">{label}</span>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 shrink-0 group-hover:text-[#0047AB]">
                  {relativeTime(c.created_at)}
                </p>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
